import React, { useEffect, useRef } from 'react';
import { HiArrowRight } from 'react-icons/hi';

const services = [
  {
    title: 'Trade Facilitation',
    desc: 'Supporting companies in navigating bilateral trade procedures, documentation, logistics coordination, and regulatory requirements between Bangladesh and Italy.',
  },
  {
    title: 'Business Networking',
    desc: 'Connecting companies with decision-makers, institutions, industry leaders, suppliers, buyers, and strategic partners through curated events and networking initiatives.',
  },
  {
    title: 'Investment Support',
    desc: 'Providing guidance on market entry, investment opportunities, business setup, and strategic expansion between Bangladesh and Italy.',
  },
  {
    title: 'Policy Dialogue and Institutional Engagement',
    desc: 'Facilitating constructive dialogue between businesses, institutions, and policymakers to encourage a more transparent and business-friendly environment.',
  },
];

function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.querySelectorAll<HTMLElement>('[data-r]').forEach((node, i) => {
            setTimeout(() => {
              node.style.opacity = '1';
              node.style.transform = 'translateY(0) translateX(0)';
            }, i * 120);
          });
          obs.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

const hide = (dir: 'up' | 'left' | 'right' = 'up'): React.CSSProperties => ({
  opacity: 0,
  transform:
    dir === 'up' ? 'translateY(28px)'
    : dir === 'left' ? 'translateX(-32px)'
    : 'translateX(32px)',
  transition: 'opacity 0.8s ease, transform 0.8s ease',
});

export default function ServicesSection() {
  const sectionRef = useReveal();

  return (
    <section id="what-we-do" className="bg-white py-20 md:py-24 font-sans">
      <div ref={sectionRef} className="w-full px-6 md:px-10 lg:px-16 xl:px-24 max-w-[1400px] mx-auto">

        {/* Section Header */}
        <div className="mb-16 md:mb-20">
          <h2
            data-r
            style={{
              ...hide('up'),
              color: '#0F1C35',
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: 800,
              letterSpacing: '-0.02em',
              marginBottom: '16px',
              textAlign: 'center',
            }}
          >
            What We Do
          </h2>

          <p
            data-r
            style={{
              ...hide('up'),
              color: '#0F1C35',
              fontSize: 'clamp(1.4rem, 2.5vw, 1.9rem)',
              fontWeight: 700,
              textAlign: 'left',
              letterSpacing: '-0.01em',
            }}
          >
            Driving Bilateral Growth
          </p>
        </div>

        {/* Top 4 Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service) => (
            <div
              key={service.title}
              data-r
              className="rounded-2xl p-8 hover:-translate-y-1 hover:shadow-xl transition-all duration-300 flex flex-col"
              style={{
                ...hide('up'),
                backgroundColor: '#F8F7F3',
                border: '1px solid #E5E7EB',
              }}
            >
              <h3 className="text-xl font-bold mb-4" style={{ color: '#0F1C35' }}>
                {service.title}
              </h3>

              <p className="text-sm leading-relaxed flex-grow" style={{ color: '#3D4A62' }}>
                {service.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Wide Bottom Card */}
        <div
          data-r
          className="mt-6 rounded-2xl p-8 md:p-10 hover:-translate-y-1 shadow-sm hover:shadow-xl transition-all duration-300"
          style={{
            ...hide('up'),
            backgroundColor: '#F8F7F3',
            border: '1px solid #E5E7EB',
          }}
        >
          <h3 className="text-2xl font-bold mb-3" style={{ color: '#0F1C35' }}>
            Legal &amp; Compliance Assistance
          </h3>
          <p className="text-sm md:text-base leading-relaxed max-w-3xl" style={{ color: '#3D4A62' }}>
            Facilitating access to legal, tax, compliance, and regulatory professionals specialized in cross-border business operations.
          </p>
        </div>

        {/* Learn More Button */}
        <div data-r className="mt-12 flex justify-center" style={hide('up')}>
          <a
            href="/services"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-white text-sm font-semibold tracking-wide transition-all duration-300 hover:brightness-110 hover:gap-3"
            style={{ backgroundColor: '#0077b6' }}
          >
            Learn More
            <HiArrowRight className="text-base" />
          </a>
        </div>

      </div>
    </section>
  );
}